import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AIChatInput, ReversePaginatedList } from "@tellescope/ui";
import { Loader2 } from "lucide-react";
import { useAskAIPageLogic } from "./useAskAIPageLogic";
import { useLoadConversation } from "./hooks";
import { ConversationList } from "./components/ConversationList";
import { EmptyState } from "./components/EmptyState";
import { CachedMessage } from "./components/CachedMessage";
import { LocalMessage } from "./components/LocalMessage";
import { PageHeader } from "../../components";
import { useSecureFileUrl } from "@tellescope/tanstack-query";

export const AskAIPage = () => {
  const { t } = useTranslation("ai");
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);

  const {
    messages: localMessages,
    isLoading,
    inputValue,
    setInputValue,
    sendMessage,
    clearConversation,
    isReady,
    currentUser,
    hasResponse,
    conversationId,
  } = useAskAIPageLogic();

  // Load conversation from cache - this is the source of truth
  // Use selectedConversationId if viewing a past conversation, otherwise use current conversationId
  const activeConversationId = selectedConversationId ?? conversationId;
  const { messages: cachedMessages, isLoading: isLoadingConversation } = useLoadConversation(activeConversationId);

  const handleNewConversation = () => {
    clearConversation();
    setSelectedConversationId(null);
  };

  const handleSelectConversation = (id: string) => {
    setSelectedConversationId(id);
  };

  // Determine if we're viewing a past conversation (not the current one being built)
  const isViewingPastConversation = selectedConversationId && selectedConversationId !== conversationId;

  // Determine if we're on the new conversation screen (no conversation started yet)
  const isNewConversation = !selectedConversationId && !conversationId;

  if (!isReady) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Process the user's avatar through secure file URL conversion
  const avatarUrl = useSecureFileUrl(currentUser?.avatar);

  const senderInfo = currentUser
    ? {
        name: `${currentUser.fname || ""} ${currentUser.lname || ""}`.trim() || "You",
        avatar: avatarUrl,
      }
    : { name: "You" };

  // Determine which messages to display - prefer cache, fall back to local for optimistic updates
  const showInput = !isViewingPastConversation && !hasResponse;

  // Determine what to render - prefer cache when available
  const hasCachedMessages = cachedMessages.length > 0;
  const displayMessages = hasCachedMessages
    ? cachedMessages.map((msg, index) => <CachedMessage key={`msg-${index}`} message={msg} senderInfo={senderInfo} />)
    : localMessages.map((msg) => <LocalMessage key={msg.id} message={msg} senderInfo={senderInfo} />);
  const hasMessages = hasCachedMessages || localMessages.length > 0;

  return (
    <div className="flex flex-col h-full min-h-[100svh]">
      <PageHeader>{t("Ask AI")}</PageHeader>
      <div className="flex flex-1 min-h-0">
        {/* Conversation List Sidebar */}
        <ConversationList
          onSelect={handleSelectConversation}
          onNewConversation={handleNewConversation}
          selectedId={selectedConversationId ?? conversationId}
          isNewConversation={isNewConversation}
        />

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col">
          {/* Loading state for conversation */}
          {activeConversationId && isLoadingConversation && !hasCachedMessages ? (
            <div className="flex-1 flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : !hasMessages ? (
            <div className="flex-1 overflow-auto p-6">
              <EmptyState />
            </div>
          ) : (
            <ReversePaginatedList className="flex-1 p-6">
              {[
                ...displayMessages,
                ...(isLoading && !isViewingPastConversation
                  ? [
                      <div
                        key="loading"
                        className="max-w-3xl mx-auto flex items-center gap-2 text-muted-foreground py-4"
                      >
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>{t("Analyzing your data...")}</span>
                      </div>,
                    ]
                  : []),
              ]}
            </ReversePaginatedList>
          )}

          {/* Input Area - hidden after receiving a response or when viewing past conversation */}
          {showInput && (
            <div className="p-4">
              <div className="max-w-3xl mx-auto">
                <AIChatInput
                  value={inputValue}
                  onChange={setInputValue}
                  onSend={sendMessage}
                  isLoading={isLoading}
                  placeholder={t("Ask a question about your patients...")}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
